import styled from "@emotion/styled";
import {
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  Radio,
  useMediaQuery
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import RadioGroup from "@material-ui/core/RadioGroup";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { useContext, useEffect, useState } from "react";
import { StoreContext } from "../context/StoreContext";
import { MOBILE_BREAKPOINT } from "../utils/constants";
import { IFilteredBy } from "./@types/FilterTypes";
import FilterIcon from "../images/vuesax/icon/FilterIcon";

const CustomizedCollectionFilterContainer = styled.div({
  display: "flex",
  margin: "30px 0",
  whiteSpace: "nowrap",
  overflow: "auto",
  // Hide scrollbar for Chrome, Safari and Opera
  "::-webkit-scrollbar": {
    display: "none"
  },
  // Hide scrollbar for IE, Edge and Firefox
  // IE and Edge
  "-ms-overflow-style": "none",
  // Firefox
  "scrollbar-width": "none"
});

const CustomizedCollectionFilterItem = styled.div<{ active?: boolean }>(
  props => ({
    lineHeight: "1.5",
    fontWeight: 600,
    color: props.active ? "var(--dark-pink)" : "var(--dark-grey)",
    cursor: "pointer",

    span: {
      margin: "0 5px",
      color: "var(--teal)"
    }
  })
);

const CustomizedFilterContainer = styled.div<{ addMarginTop: boolean }>(
  props => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "20px",
    marginTop: props.addMarginTop ? "30px" : 0,

    h6: {
      fontSize: "1.6rem",
      fontWeight: 700,
      lineHeight: "1.5",
      margin: 0
    }
  })
);

const Filters = ({
  filterTags,
  applyFilters,
  shouldRenderFilters,
  defaultCollectionFilter,
  shouldShowDefaultFilter = false,
  totalProducts
}: {
  filterTags: string[];
  applyFilters: (filter: IFilteredBy | null) => void;
  shouldRenderFilters: boolean;
  defaultCollectionFilter?: string;
  shouldShowDefaultFilter?: boolean;
  totalProducts: number;
}) => {
  const extractFilterValues = (filterKey: string) => {
    try {
      const values = filterTags
        .filter(item => item.startsWith(filterKey))
        ?.map(item => item?.replace(`${filterKey}:`, "").trim())
        .filter(item => item ?? item);
      return values;
    } catch (error) {
      return null;
    }
  };

  const isBrowser = typeof window !== "undefined";
  const params = new URLSearchParams(isBrowser ? window.location.search : "");

  const colorways = extractFilterValues("color");
  const collections = extractFilterValues("collection");
  const effects = extractFilterValues("effect");

  const [filteredBy, setFilteredBy] = useState<IFilteredBy | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    const defaultCollection = params.get("Collection")
      ? params.get("Collection")
      : defaultCollectionFilter &&
        collections?.includes(defaultCollectionFilter) &&
        shouldShowDefaultFilter
        ? defaultCollectionFilter
        : null;

    setFilteredBy({
      Color: params.get("Color"),
      Effect: params.get("Effect"),
      Collection: defaultCollection,
      SortOrder: "best"
    });
  }, [shouldShowDefaultFilter]);

  useEffect(() => {
    applyFilters(filteredBy);
    setTimeout(() => {
      toggleLoading(false);
    }, 500);
  }, [filteredBy]);

  const { toggleLoading } = useContext(StoreContext);

  const filterBy = (filterKey: string, value: string | null) => {
    toggleLoading(true);
    value && params.set(filterKey, value);
    if (isBrowser)
      window.history.replaceState({}, "", `${location.pathname}?${params}`);
    const key = filterKey as keyof IFilteredBy;

    if (filteredBy && filteredBy[key] === value) {
      setFilteredBy({
        ...filteredBy,
        [key]: null
      });
    } else {
      setFilteredBy({
        ...filteredBy,
        [key]: value
      });
    }
  };

  const resetFilters = () => {
    toggleLoading(true);
    params.delete("Color");
    params.delete("Effect");
    params.delete("Collection");
    if (isBrowser) window.history.replaceState({}, "", location.pathname);
    setFilteredBy(null);
  };

  // Some unique colornames which don't exist in browser
  const colorMap: Record<string, string> = {
    peach: "#FFB59B",
    nude: "tan"
  };

  return (
    <>
      {shouldRenderFilters && (
        <CustomizedCollectionFilterContainer>
          <CustomizedCollectionFilterItem
            onClick={() => {
              filterBy("Collection", null);
            }}
            active={filteredBy?.Collection === null || filteredBy === null}
          >
            ALL<span>|</span>
          </CustomizedCollectionFilterItem>
          {collections?.map((collection, index) => (
            <CustomizedCollectionFilterItem
              key={collection + index.toString()}
              onClick={() => {
                filteredBy?.Collection === collection
                  ? filterBy("Collection", null)
                  : collection && filterBy("Collection", collection);
              }}
              active={filteredBy?.Collection === collection}
            >
              {collection.toUpperCase()}
              {index !== collections.length - 1 && <span>|</span>}
            </CustomizedCollectionFilterItem>
          ))}
        </CustomizedCollectionFilterContainer>
      )}
      <CustomizedFilterContainer addMarginTop={!shouldRenderFilters}>
        <h6>{totalProducts}&nbsp;Products</h6>
        <FilterDrawer
          shouldRenderFilters={shouldRenderFilters}
          filteredBy={filteredBy}
          colorways={colorways}
          filterBy={filterBy}
          colorMap={colorMap}
          effects={effects}
          resetFilters={resetFilters}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      </CustomizedFilterContainer>
    </>
  );
};

export default Filters;

const useStyles = makeStyles(theme => ({
  button: {
    border: "1px solid var(--dark-grey)",
    borderRadius: "50px",
    padding: "10px 20px"
  },

  buttonClear: {
    border: "1px solid black",
    color: "black"
  },

  buttonApply: {
    backgroundColor: "black",
    color: "white"
  },

  customPaper: {
    maxWidth: "90%",

    "@media (min-width: 768px)": {
      maxWidth: "506px"
    }
  },

  customCheckbox: {
    padding: 0,
    "& .MuiSvgIcon-root": {
      fill: "black" // Change the color to black
    },
    "&.Mui-checked.MuiCheckbox-colorPrimary": {
      "& .MuiSvgIcon-root": {
        fill: "black" // Change the color to black for the checked state
      }
    }
  },

  customRadio: {
    gap: "15px",

    "& .MuiFormControlLabel-root": {
      margin: 0,
      gap: "15px",
      "& .MuiTypography-root": {
        fontFamily: "Open Sans", // Set the font-family to "Open Sans"
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: "400"
      }
    },

    "& .MuiRadio-root": {
      padding: 0 // Set padding to 0 to remove the default padding
    },
    "& .MuiSvgIcon-root": {
      fill: "black" // Change the color to black
    },
    "&.Mui-checked.MuiCheckbox-colorPrimary": {
      "& .MuiSvgIcon-root": {
        fill: "black" // Change the color to black for the checked state
      }
    }
  }
}));

const CustomDrawerContainer = styled.div({
  padding: "15px 0 40px",

  h6: {
    color: "black",
    margin: "20px 0"
  },

  ".close-icon": {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 15px"
  }
});

const CustomizedSortFilter = styled.div({
  padding: "0 15px",
  margin: "20px 0",

  "@media(min-width: 768px)": {
    padding: "0 180px 0 59px"
  }
});

const CustomizedColorFilterContainer = styled.div({
  padding: "0 15px",
  margin: "20px 0",

  ".color-swatch": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  "@media(min-width: 768px)": {
    padding: "0 180px 0 59px"
  }
});

const CustomizedEffectFilterContainer = styled.div({
  padding: "0 15px",

  ".effect-items": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    rowGap: "15px",

    ".effect-item": {
      color: "black",
      lineHeight: "1.5",
      display: "flex",
      alignContent: "center",
      gap: "15px",
      gridColumn: "span 2"
    }
  },

  "@media(min-width: 768px)": {
    padding: "0 180px 0 59px"
  }
});

const CustomizedButtonContainer = styled.div({
  padding: "0 15px",
  marginTop: "30px",
  display: "flex",
  justifyContent: "center",
  gap: "20px",

  button: {
    width: "124px",
    borderRadius: "50px",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "1.5",
    padding: "10px 0"
  }
});

const FilterDrawer = ({
  shouldRenderFilters,
  colorways,
  filteredBy,
  filterBy,
  colorMap,
  effects,
  resetFilters,
  isDrawerOpen,
  setIsDrawerOpen
}: {
  shouldRenderFilters: boolean;
  colorways: string[] | null;
  filteredBy: IFilteredBy | null;
  filterBy: (filterKey: string, value: string | null) => void;
  colorMap: Record<string, string>;
  effects: string[] | null;
  resetFilters: () => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (openstate: boolean) => void;
}) => {
  const classes = useStyles();

  const isMobile = useMediaQuery(MOBILE_BREAKPOINT, { noSsr: true });

  const toggleDrawer = (openState: boolean) => () => {
    setIsDrawerOpen(openState);
  };

  return (
    <>
      <Button
        classes={{ root: classes.button }}
        onClick={toggleDrawer(true)}
        endIcon={<FilterIcon />}
      >
        {shouldRenderFilters ? "Filters" : "Sort"}
      </Button>
      <Drawer
        classes={{
          paper: classes.customPaper
        }}
        anchor={isMobile ? "left" : "right"}
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
      >
        <CustomDrawerContainer>
          <div className="close-icon" onClick={toggleDrawer(false)}>
            <HighlightOffIcon />
          </div>
          <CustomizedSortFilter>
            <h6 className="subtitle text-uppercase">SORT BY</h6>
            <RadioGroup
              aria-label="sort order"
              name="sort order"
              value={filteredBy && filteredBy["SortOrder"]}
              onChange={e => {
                filterBy("SortOrder", e.target.value);
              }}
              classes={{ root: classes.customRadio }}
            >
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Newest"
              />
              <FormControlLabel
                value="best"
                control={<Radio />}
                label="Best Seller"
              />
              <FormControlLabel
                value="low"
                control={<Radio />}
                label="Price Low-High"
              />
              <FormControlLabel
                value="high"
                control={<Radio />}
                label="Price High-Low"
              />
            </RadioGroup>
          </CustomizedSortFilter>
          <Divider />
          {shouldRenderFilters && (
            <>
              <CustomizedColorFilterContainer>
                <h6 className="subtitle text-uppercase">{`COLOR (${colorways?.length})`}</h6>
                <div className="filter-items colorway">
                  {colorways?.map(color => (
                    <div
                      className="filter-item color-item"
                      key={color}
                      onClick={() => {
                        color && filterBy("Color", color);
                      }}
                    >
                      <span
                        style={
                          color
                            ? {
                              backgroundColor: `${colorMap[color.trim()]
                                  ? colorMap[color.trim()]
                                  : color
                                }`,
                              border:
                                color === "white"
                                  ? "1px solid var(--light-grey)"
                                  : "none"
                            }
                            : {}
                        }
                        className="color-swatch"
                      >
                        {filteredBy?.Color === color && (
                          <CheckIcon
                            fontSize="small"
                            htmlColor={color === "white" ? "black" : "white"}
                          />
                        )}
                      </span>
                      {color}
                    </div>
                  ))}
                </div>
              </CustomizedColorFilterContainer>
              <Divider />
              <CustomizedEffectFilterContainer>
                <h6 className="subtitle text-uppercase">{`EFFECT (${effects?.length})`}</h6>
                <div className="effect-items">
                  {effects?.map((effect, index) => (
                    <div
                      className="effect-item"
                      key={effect + index.toString()}
                      onClick={() => {
                        effect && filterBy("Effect", effect);
                      }}
                    >
                      <Checkbox
                        classes={{ root: classes.customCheckbox }}
                        checked={
                          (filteredBy && filteredBy["Effect"] === effect) ||
                          false
                        }
                      />
                      {effect.toUpperCase()}
                    </div>
                  ))}
                </div>
              </CustomizedEffectFilterContainer>
            </>
          )}
          <CustomizedButtonContainer>
            <Button
              classes={{ root: classes.buttonClear }}
              onClick={resetFilters}
            >
              Clear All
            </Button>
            <Button
              classes={{ root: classes.buttonApply }}
              onClick={toggleDrawer(false)}
            >
              Apply
            </Button>
          </CustomizedButtonContainer>
        </CustomDrawerContainer>
      </Drawer>
    </>
  );
};
